import { fromJS, type List } from 'immutable';
import { stringify, type ParsedQuery } from 'query-string';
import { fetchCatch } from 'src/core/sentry';
import type { CancelValues, DateTimeKeys, Trip, Trips, UserRole } from 'src/types/trips/Trips';
import type { Immutable } from 'src/types/ImmutableMap';
import captureException from 'src/common/utils/captureException';
import { PICKUP_TIME_FIELD, TRIP_ENDS_AT_FIELD, TRIP_STARTS_AT_FIELD } from 'src/inbox/constants';
import type { AppDispatch } from 'src/common/hooks/useAppDispatch';
import { USER_ROLES } from 'src/common/constants';
import { apiFetchThunk, backendFetchThunk } from '../../common/ducks/fetch';

/**
 * @typedef {import('../../types/trips/Trips').ImmutableTrip} ImmutableTrip
 */

// app/reducer/action
export const SET_TRIPS = 'inbox/trips/SET_TRIPS';
export const APPEND_TRIPS = 'inbox/trips/APPEND_TRIPS';
export const REMOVE_TRIP = 'inbox/trips/REMOVE_TRIP';
export const UPDATE_TRIP = 'inbox/trips/UPDATE_TRIP';
export const CLEAR_TRIPS = 'inbox/trips/CLEAR_TRIPS';

type SetTrips = {
  type: typeof SET_TRIPS;
  trips: Trips;
  sortByTripStart: boolean;
};
type AppendTrips = {
  type: typeof APPEND_TRIPS;
  trips: Immutable<Trips>;
  sortByTripStart: boolean;
};
type UpdateTrip = {
  type: typeof UPDATE_TRIP;
  trip: Immutable<Trip>;
};
type RemoveTrip = {
  type: typeof REMOVE_TRIP;
  tripId: number | string;
};
type ClearTrips = {
  type: typeof CLEAR_TRIPS;
};

export const setTrips = (trips: Trips, sortByTripStart?: boolean): SetTrips => (
  { type: SET_TRIPS, trips, sortByTripStart: Boolean(sortByTripStart) }
);
export const appendTrips = (trips: Immutable<Trips>, sortByTripStart?: boolean): AppendTrips => (
  { type: APPEND_TRIPS, trips, sortByTripStart: Boolean(sortByTripStart) }
);
export const updateTrip = (trip: Immutable<Trip>): UpdateTrip => (
  { type: UPDATE_TRIP, trip }
);
export const removeTrip = (tripId: Trip['pk']): RemoveTrip => (
  { type: REMOVE_TRIP, tripId }
);
export const clearTrips = (): ClearTrips => (
  { type: CLEAR_TRIPS }
);

const transformResults = (
  results: List<Immutable<Trip>>,
  /** Default sort is date_last_message, i.e. threads with newest message activity first */
  sortByTripStart = false,
) => {
  const trips = results.map((trip) => {
    if (!trip) return trip;
    const startDateTime = trip.get(TRIP_STARTS_AT_FIELD, '');
    const endDateTime = trip.get(TRIP_ENDS_AT_FIELD, '');
    if (!startDateTime || !endDateTime) {
      captureException('Expect trip_starts_at and trip_ends_at to always be defined', { extra: { pk: trip.get('pk') } });
    }
    return trip.merge({
      pickup_time: startDateTime.substring(11),
      return_time: endDateTime.substring(11),
      end_date: endDateTime.substring(0, 10),
      pickup_time_pristine: trip.get(PICKUP_TIME_FIELD),
    } as Omit<DateTimeKeys, 'preferred_date'>); // preferred_date is already reliable, no mutation needed
  });

  return sortByTripStart
    ? results.sortBy(trip => {
      if (!trip) return 0;
      return new Date(trip.get(TRIP_STARTS_AT_FIELD)).getTime();
    })
    : trips.sort((tripA, tripB) => {
      if (!tripA || !tripB) return 0;
      const tripALastMessageDate = new Date(tripA.get('date_last_message')).getTime();
      const tripBLastMessageDate = new Date(tripB.get('date_last_message')).getTime();
      return tripALastMessageDate - tripBLastMessageDate || tripA.get('pk') - tripB.get('pk');
    }).reverse();
};

// Just for clarity's sake
const INITIAL_TRIPS_STATE = fromJS({
  results: [],
  count: 0,
  next: null,
  previous: null,
}) as Immutable<Trips>;

export default (
// eslint-disable-next-line @typescript-eslint/default-param-last
  trips = INITIAL_TRIPS_STATE,
  action: SetTrips | AppendTrips | RemoveTrip | ClearTrips | UpdateTrip,
) => {
  switch (action.type) {
    case SET_TRIPS:
      return fromJS(action.trips)
        .update('results', (results: List<Immutable<Trip>>) => transformResults(results, action.sortByTripStart));
    case APPEND_TRIPS:
      return trips
      // TODO: also need to figure out if this will still work
        .update('results', (results: List<Immutable<Trip>>) => results
          // Get rid of trips that were previously in the list that are
          // also in the new results
          .filterNot((trip?: Immutable<Trip>) => {
            if (!trip) return false;
            return Boolean(action.trips.get('results').find(newTrip => newTrip?.get('pk') === trip.get('pk')));
          })
          .concat(action.trips.get('results')))
        .update('results', (results: List<Immutable<Trip>>) => transformResults(results, action.sortByTripStart))
      // Merge in all the things that aren't the results
        .merge(action.trips.delete('results'));
    case REMOVE_TRIP:
      return trips.update('results', (results: List<Immutable<Trip>>) => results.filterNot(trip => trip?.get('pk') === action.tripId));
    case UPDATE_TRIP: {
      const tripIndex = trips.get('results').findIndex(trip => trip?.get('pk') === action.trip.get('pk'));
      return tripIndex > -1 // trips list contains the trip
        // update that trip
        ? trips.mergeIn(['results', tripIndex], action.trip)
          .update('results', (results: List<Immutable<Trip>>) => transformResults(results))
        // Append new trip to the end of trips list otherwise
        : trips.setIn(['results', trips.get('results').size], action.trip)
          .update('results', (results: List<Immutable<Trip>>) => transformResults(results));
    }
    case CLEAR_TRIPS:
      return INITIAL_TRIPS_STATE;
    default:
      return trips;
  }
};

const tripsApiBaseUrl = '/trips/';

// Get should use append - the only time we want to blow away trips is when we're switching filters.
export const getTripsCall = (
  { filter, nextUrl }: { filter: ParsedQuery['filter']; nextUrl?: string },
) => async dispatch => {
  const url = `${tripsApiBaseUrl}?${stringify({ filter })}`;
  const response = await (
    nextUrl ? dispatch(backendFetchThunk(nextUrl)) : dispatch(apiFetchThunk(url))
  );
  if (!response.ok) {
    throw response;
  }
  const trips = await response.json();
  dispatch(appendTrips(fromJS(trips), filter === 'upcoming'));
};

// This uses setTrips because we want trips to be blown away when the filter
// changes.
export const getNewFilterSetCall = ({ filter }: ParsedQuery) => async dispatch => {
  const url = `${tripsApiBaseUrl}?${stringify({ filter })}`;
  const response = await dispatch(apiFetchThunk(url));
  if (!response.ok) {
    throw response;
  }
  const trips = await response.json() as Trips;
  dispatch(setTrips(trips, filter === 'upcoming'));
  return trips;
};

// Handles all of the wrapping logic around network handling and merging
// a returned trip into the store
const tripUpdateWrapper = fetchCreator => (...args) => (
  dispatch => (
    dispatch(fetchCreator(...args))
      .then(response => {
        if (!response.ok) {
          throw response;
        }
        return response.json();
      })
      .then(tripObject => {
        const trip = fromJS(tripObject);
        dispatch(updateTrip(trip));
        return trip;
      })
  )
);

/**
 * @type {(pk: number | string, queryParams?: Record<string, any>) => Promise<ImmutableTrip>  }
 */
export const getTripCall = tripUpdateWrapper(
  (pk, queryParams) => apiFetchThunk(
    `/trips/${pk}/?${stringify(queryParams)}`,
  ),
);

/**
 * @type {(pk: number | string, formData: Record<string, any>) => Promise<ImmutableTrip>  }
 */
export const cancelTripCall = tripUpdateWrapper(
  (pk, formData) => apiFetchThunk(
    `/trips/${pk}/cancel/`,
    {
      method: 'PATCH',
      body: JSON.stringify(formData),
    },
  ),
);

/**
 * @type {(pk: number | string, values: Record<string, any>) => Promise<ImmutableTrip>  }
 */
export const sendOfferCall = tripUpdateWrapper(
  (pk, values) => apiFetchThunk(
    `/trips/${pk}/offer/`,
    {
      method: 'POST',
      body: JSON.stringify(values),
    },
  ),
);

/**
 * @type {(pk: number | string, values: Record<string, any>) => Promise<unknown>  }
 */
export const cancelOfferCall = tripUpdateWrapper(
  (pk, values) => apiFetchThunk(
    `/trips/${pk}/offer/`,
    {
      method: 'DELETE',
      body: JSON.stringify(values),
    },
  ),
);

/**
 * @type {(pk: number | string, values: Record<string, any>) => Promise<ImmutableTrip>  }
 */
export const requestOfferChanges = tripUpdateWrapper(
  (pk, values) => apiFetchThunk(
    `/trips/${pk}/request-changes/`,
    {
      method: 'POST',
      body: JSON.stringify(values),
    },
  ),
);

/**
 * @type {(pk: number | string) => Promise<unknown>  }
 */
export const cancelRequestedChangesCall = tripUpdateWrapper(
  pk => apiFetchThunk(
    `/trips/${pk}/request-changes/`,
    {
      method: 'DELETE',
    },
  ),
);

/**
 * Creates a Redux thunk action that declines a trip inquiry.
 * This function is wrapped with tripUpdateWrapper to handle common trip update operations.
 *
 * @param pk - The primary key (ID) of the trip to decline
 * @param values - Object containing the form values with decline details
 * @param values.cancel_reason - The reason for declining which is selected by owner from dropdown
 * @param values.message - Additional message provided by owner explaining the decline
 * @returns A Redux thunk action that when dispatched will make an API call to decline the inquiry
 */
export const declineInquiryCall = tripUpdateWrapper(
  (pk: number | string, values: CancelValues) => apiFetchThunk(
    `/trips/${pk}/decline/`,
    {
      method: 'PATCH',
      body: JSON.stringify(values),
    },
  ),
);

/**
 * Creates a Redux thunk action to cancel a confirmed reservation.
 * This function is wrapped with tripUpdateWrapper to handle common trip update operations.
 *
 * @param pk - The primary key or ID of the trip to cancel
 * @param values - Object containing the form values with decline details
 * @param values.cancel_reason - The reason for declining which is selected by owner from dropdown
 * @param values.message - Additional message provided by owner explaining the decline
 * @returns A Redux thunk action that sends a PATCH request to the cancel endpoint
 */
export const cancelReservationCall = tripUpdateWrapper(
  (pk: number | string, values: CancelValues) => (
    apiFetchThunk(
      `/trips/${pk}/cancel-confirmed/`,
      {
        method: 'PATCH',
        body: JSON.stringify(values),
      },
    )
  ),
);

/**
 * Fetches cancellation reasons for boat owners
 *
 * This function creates a Redux thunk that performs an API request to retrieve
 * the list of cancellation reasons available to boat owners.
 *
 * @returns {Promise<any>} A promise that resolves to the list of cancellation reasons
 * @throws {Response} Throws the response object if the API request fails
 */
export const getCancellationReasonsCall = (
  tripId: Trip['pk'],
  userRole: UserRole = USER_ROLES.OWNER,
) => async (dispatch: AppDispatch) => {
  const response = await dispatch(apiFetchThunk(
    `/trips/${tripId}/cancel-reasons/${userRole}/`,
  ));
  if (!response.ok) {
    throw response;
  }
  const reasons = await response.json();
  return reasons;
};

/**
 * @typedef {import('redux').Dispatch} Dispatch
 * @typedef {(tripId: number) => (dispatch: Dispatch) => Promise<ImmutableTrip>} Archiver
 * @type {(action: 'archive' | 'unarchive') => Archiver}
 */
export const tripArchiver = action => tripId => dispatch => dispatch(apiFetchThunk(
  `/trips/${tripId}/${action}/`,
  { method: 'PATCH' },
))
  .then(response => {
    if (!response.ok) {
      throw response;
    }
    return response.json();
  })
// Is it going to work to just remove it from the view?
  .then(() => dispatch(removeTrip(tripId)))
  .catch(fetchCatch);

export const archiveTripCall = tripArchiver('archive');
export const unarchiveTripCall = tripArchiver('unarchive');
